/**
 * moduleRoutes.ts — which module owns which part of the app.
 *
 * WHY A PREFIX TABLE RATHER THAN A PROP ON EVERY ROUTE
 * The alternative was `<ProtectedRoute module="crm">` on each of ~100 route lines. That
 * fails the way gates always fail: somebody adds a page and forgets the prop, and a page
 * with no gate looks exactly like a page that is meant to be open. With a prefix table a
 * NEW page under /crm/ is gated the moment it exists, without anybody remembering anything.
 *
 * It is also the honest shape of the product: modules own AREAS of the app, not individual
 * screens. The sidebar already groups them this way.
 *
 * ── LONGEST PREFIX WINS, AND THAT IS LOAD-BEARING ──────────────────────────────────
 * Leave is a module in its own right AND lives inside the HR area, so `hr/my-leave` must
 * resolve to `leave` while `hr/employees` resolves to `hr`. The lookup sorts by length and
 * takes the first match, so adding a more specific entry never needs the general one moved.
 *
 * ── WHAT IS DELIBERATELY NOT GATED ─────────────────────────────────────────────────
 * `/dashboard` (it is the redirect target — gating it would loop), `/profile`, `/settings`,
 * `/notifications`, `/users`, `/owner/*`, most of `/admin/*`, and `/company/*`. Those are
 * either cross-cutting or administrative: a company that bought only CRM still has people,
 * settings and a company directory. Assets and the two leave-admin pages ARE gated, because
 * each is the admin surface of a module that can be sold separately.
 */

import type { ModuleKey } from '../types';

/** URL prefix (no leading slash — matched against a normalised pathname) → owning module. */
export const MODULE_ROUTE_PREFIXES: ReadonlyArray<{ prefix: string; module: ModuleKey }> = [
  // Leave first in intent (the lookup sorts, so order here is for readers, not behaviour).
  { prefix: 'hr/balances',           module: 'leave' },
  { prefix: 'hr/calendar',           module: 'leave' },
  { prefix: 'hr/leaves',             module: 'leave' },
  { prefix: 'hr/my-leave',           module: 'leave' },
  { prefix: 'hr/team-leave',         module: 'leave' },
  { prefix: 'admin/leave-balances',  module: 'leave' },
  { prefix: 'admin/leave-policies',  module: 'leave' },

  { prefix: 'people/recruitment',    module: 'recruitment' },
  { prefix: 'admin/assets',          module: 'assets' },

  { prefix: 'crm/',                  module: 'crm' },
  { prefix: 'marketing/',            module: 'marketing' },
  { prefix: 'finance/',              module: 'finance' },
  { prefix: 'support/',              module: 'support' },
  { prefix: 'hr/',                   module: 'hr' },
  { prefix: 'people/',               module: 'hr' },
  { prefix: 'work/',                 module: 'projects' },
  { prefix: 'performance',           module: 'performance' },
  { prefix: 'kpi/',                  module: 'performance' },
  { prefix: 'analytics/',            module: 'analytics' },
  { prefix: 'communication/',        module: 'communication' },

  /**
   * ⛔ THERE IS NO `mail` ENTRY HERE, AND ITS ABSENCE IS THE RULING.
   *
   * Lis, 2026-09-09, restated 2026-10-05: *"Webmail will be for all employees regardless
   * of the feature they are using. Apps in general will be for all."* Webmail, My tasks,
   * Chat and Directory are APPS. Every employee in every workspace always gets them, so
   * `/mail` is owned by NO module and the router must never refuse it.
   *
   * ⚠️ A `{ prefix: 'mail', module: 'mail' }` ENTRY WAS HERE BETWEEN #567 AND THIS
   * CHANGE, and it is why one real person — the employee in workspace `..0002`, whose
   * company has no enabled `mail` row — was redirected from `/mail` to `/dashboard`.
   * Measured on production before the fix: `current_user_module_access()` answered
   * `mail = none` for them.
   *
   * ⛔ DO NOT RE-ADD IT. `src/lib/__tests__/webmailIsAnApp.test.ts` fails if anybody
   * does, which is the point: this ruling was made in conversation on 2026-09-09 and
   * contradicted by two merged PRs because it existed in no file a session reads.
   *
   * ⚠️ My tasks and Chat are a DIFFERENT case and stay gated: they point into
   * `/work/*` and `/communication/*`, which are real sellable modules. The ruling forbids
   * gating WEBMAIL, not pretending those areas are open.
   */
];

/** Sorted once, longest first, so the most specific entry always wins. */
const SORTED = [...MODULE_ROUTE_PREFIXES].sort((a, b) => b.prefix.length - a.prefix.length);

/**
 * The module that owns `pathname`, or null if the area is not module-gated.
 *
 * Tolerates a leading slash and a trailing one; everything else is compared verbatim, so
 * `/crm/contacts` matches `crm/` and `/crmsomething` does not match anything (the trailing
 * slash in the prefix is what prevents that).
 */
export function moduleForPath(pathname: string): ModuleKey | null {
  const path = (pathname ?? '').replace(/^\/+/, '');
  for (const entry of SORTED) {
    if (path === entry.prefix || path.startsWith(entry.prefix)) return entry.module;
  }
  return null;
}
